import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Leaf } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const user = await login(email, password);
      toast.success("Bem-vindo(a) de volta.");
      const redirectTo = location?.state?.redirectTo;
      const openBooking = location?.state?.openBooking;
      if (user?.role === "admin") navigate("/admin");
      else if (redirectTo) navigate(redirectTo, { state: openBooking ? { openBooking: true } : undefined });
      else navigate("/app");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-30" />
        <div className="relative h-full p-12 flex flex-col justify-between text-primary-foreground">
          <Link to="/" className="flex items-center gap-3" data-testid="login-brand-link">
            <div className="w-10 h-10 rounded-sm bg-primary-foreground text-primary grid place-items-center">
              <Leaf className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <div className="brand-wordmark font-bold text-base uppercase">Centro de Performance</div>
              <div className="text-xs text-gold opacity-90 overline tracking-[0.22em]">Farmacêutica</div>
            </div>
          </Link>
          <div>
            <div className="overline opacity-80 mb-4">Acompanhamento clínico</div>
            <h2 className="font-heading text-4xl font-bold leading-tight">
              Protocolo por fases.<br />Check-ins semanais.<br />Resultado clínico.
            </h2>
          </div>
          <p className="text-sm opacity-80 max-w-sm">
            Plataforma farmacêutica de protocolos contínuos — com acompanhamento humano, análise de exames e monitoramento de aplicações.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="overline text-muted-foreground mb-2">Entrar</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Acesse seu painel</h1>
            <p className="text-sm text-muted-foreground mt-2">Continue seu acompanhamento clínico.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="overline text-muted-foreground mb-2 block">Email</label>
              <input
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="seu@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="overline text-muted-foreground mb-2 block">Senha</label>
              <input
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-sm border border-input bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}

          <button
            type="submit" disabled={loading}
            className="w-full rounded-sm bg-primary text-primary-foreground px-4 py-3 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            data-testid="login-submit-btn"
          >
            {loading ? "Entrando…" : "Entrar"}
          </button>

          <div className="text-sm text-muted-foreground text-center">
            Novo aqui? <Link to="/cadastro" className="text-primary font-medium hover:underline" data-testid="login-to-register">Criar conta</Link>
          </div>

          <div className="rounded-sm border border-dashed border-border bg-muted/40 p-4 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground mb-1">Conta de demonstração</div>
            paciente@clubesaude.com · paciente123
          </div>
        </form>
      </div>
    </div>
  );
}
